/**
 * audio.ts — the glockenspiel synth.
 *
 * Calibrated against RECORDINGS of the physical Royal Percussion concert
 * glockenspiel (2026-08: F4/F5/C5/F6 bars, single strikes, damper open).
 * Measured facts the voicing now matches:
 *   - the bars are arch-tuned: first overtone at 4.0× the fundamental
 *     (double-octave tuning; measured 4.008–4.015), NOT the free-free 2.76×
 *   - extremely pure: the 4× partial is ~6–10% of the fundamental, and no
 *     other partial rises above the noise
 *   - very long ring: fundamental T60 ≈ 7–30 s depending on bar/pedal; the
 *     4× partial dies in ~1.5–2 s
 *   - tuning is A440-exact (all four bars within ±0.04 semitone)
 */
import { midiToFreq } from '@windchime/engine';

const PARTIALS = [
  { r: 1.0, a: 1.0, d: 8.0 }, // fundamental — pure, very long ring (measured T60 up to ~30s; kept musical)
  { r: 4.02, a: 0.08, d: 1.8 }, // the arch-tuned double-octave partial (measured 1:4, ~6-10%, ~2s)
];

export class ChimeAudio {
  private ctx?: AudioContext;
  private master?: GainNode;
  private comp?: DynamicsCompressorNode;
  private toneOut?: BiquadFilterNode;
  private wet?: GainNode;
  private dry?: GainNode;
  private conv?: ConvolverNode;
  private started = false;
  private volume = 0.7;

  private makeImpulse(ctx: AudioContext, seconds: number): AudioBuffer {
    const len = ctx.sampleRate * seconds,
      buf = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let c = 0; c < 2; c++) {
      const d = buf.getChannelData(c);
      // warm tail: noise that decays, with progressively less high-frequency energy
      let lp = 0;
      for (let i = 0; i < len; i++) {
        const n = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.6);
        lp += (n - lp) * 0.45;
        d[i] = lp;
      }
    }
    return buf;
  }

  init(): void {
    if (this.started) return;
    this.started = true;
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AC();
    this.ctx = ctx;
    const master = ctx.createGain();
    master.gain.value = this.volume;
    this.master = master;
    const toneOut = ctx.createBiquadFilter();
    toneOut.type = 'lowpass';
    toneOut.frequency.value = 9000; // darker master tone (was 12k)
    toneOut.Q.value = 0.3; // tame digital glare
    this.toneOut = toneOut;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -18;
    comp.knee.value = 24;
    comp.ratio.value = 4;
    comp.attack.value = 0.003;
    comp.release.value = 0.28;
    this.comp = comp;
    const wet = ctx.createGain();
    wet.gain.value = 0.4; // a touch more space for a big instrument
    this.wet = wet;
    const dry = ctx.createGain();
    dry.gain.value = 0.9;
    this.dry = dry;
    const conv = ctx.createConvolver();
    conv.buffer = this.makeImpulse(ctx, 3.6);
    this.conv = conv;
    dry.connect(comp);
    conv.connect(wet);
    wet.connect(comp);
    comp.connect(toneOut);
    toneOut.connect(master);
    master.connect(ctx.destination);
  }

  resume(): void {
    if (this.ctx && this.ctx.state === 'suspended') this.ctx.resume();
  }

  suspend(): void {
    if (this.ctx && this.ctx.state === 'running') this.ctx.suspend();
  }

  get running(): boolean {
    return this.ctx?.state === 'running';
  }

  setVolume(v: number): void {
    this.volume = v;
    if (this.master) this.master.gain.value = v;
  }

  strike(midi: number, vel /*0..1*/: number): void {
    const ctx = this.ctx,
      dry = this.dry,
      conv = this.conv;
    if (!ctx || !dry || !conv) return;
    const t = ctx.currentTime,
      f0 = midiToFreq(midi);
    const bright = 0.3 + 0.5 * vel; // hard hits excite upper modes (darkened)
    const peak = 0.05 + 0.15 * vel;
    const reg = Math.max(0.85, Math.min(1.7, 1 + (84 - midi) / 40)); // lower bars ring longer
    const send = ctx.createGain();
    send.gain.value = 1;
    // per-voice tone opens with velocity, so soft strikes are mellow, hard ones bright
    const tone = ctx.createBiquadFilter();
    tone.type = 'lowpass';
    tone.frequency.value = Math.min(12000, f0 * (2.5 + 6 * vel) + 900); // darker knee
    tone.Q.value = 0.4;
    send.connect(tone);
    tone.connect(dry);
    tone.connect(conv);
    PARTIALS.forEach((p, i) => {
      const dec = p.d * reg * (0.8 + 0.2 * vel);
      const amp = peak * p.a * (i === 0 ? 1 : bright);
      // give the fundamental a subtly detuned twin for a living, beating shimmer
      const cents = i === 0 ? [-0.7, +0.7] : [0];
      cents.forEach((c) => {
        const o = ctx.createOscillator();
        o.type = 'sine';
        o.frequency.value = f0 * p.r * Math.pow(2, c / 1200);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(amp / cents.length, t + 0.002);
        g.gain.exponentialRampToValueAtTime(0.0001, t + dec);
        o.connect(g);
        g.connect(send);
        o.start(t);
        o.stop(t + dec + 0.05);
      });
    });
    // mallet contact: short, rounded chiff (not an ice-pick ping)
    const nlen = Math.floor(ctx.sampleRate * 0.035);
    const nb = ctx.createBuffer(1, nlen, ctx.sampleRate);
    const nd = nb.getChannelData(0);
    for (let i = 0; i < nlen; i++) nd[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / nlen, 2);
    const ns = ctx.createBufferSource();
    ns.buffer = nb;
    const nf = ctx.createBiquadFilter();
    nf.type = 'bandpass';
    nf.frequency.value = Math.min(10000, f0 * 2.2); // rounder mallet contact (was 3.5×)
    nf.Q.value = 1.3;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(peak * 0.3 * bright, t);
    ng.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
    ns.connect(nf);
    nf.connect(ng);
    ng.connect(send);
    ns.start(t);
    ns.stop(t + 0.06);
  }
}
